import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Badge,
  Pagination,
  LinearProgress,
  Chip,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSearchParams } from 'react-router-dom';
import { fetchPublicDWStateElections } from '../../utils/democracyWorksApi';
import { getStateName } from '../../utils/stateLegislationApi';
import {
  extractMeasuresFromElections,
  filterBallotMeasures,
  getLocalJurisdictions,
  paginateMeasures,
  buildBallotListSearchParams,
  parseBallotListUrl,
  BALLOT_SCOPE_CHIP_OPTIONS,
  BALLOT_SCOPE_LOCAL,
  BALLOT_SCOPE_STATE,
  BALLOT_SCOPE_DEFAULT,
  normalizeBallotScope,
  getBallotMeasuresEmptyState,
} from '../../utils/ballotMeasuresApi';
import BillsSearchBar from './BillsSearchBar';
import BallotMeasureCard from './BallotMeasureCard';
import BallotMeasuresFilterModal from './BallotMeasuresFilterModal';

const PAGE_SIZE = 15;
const PRIMARY = '#047857';

function normalizeSearchParamsForCompare(sp) {
  return [...sp.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
}

export default function BallotMeasuresPanel({ userState, onUrlChange }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const initial = useMemo(() => {
    const parsed = parseBallotListUrl((searchParams.toString() || ''));
    const defaultState = parsed.stateCode || userState || '';
    return {
      stateCode: defaultState,
      scope: parsed.scope,
      q: parsed.q,
      places: parsed.places,
      page: parsed.page,
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedState, setSelectedState] = useState(initial.stateCode);
  const [scope, setScope] = useState(initial.scope);
  const [searchQuery, setSearchQuery] = useState(initial.q);
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(initial.q);
  const [appliedPlaces, setAppliedPlaces] = useState(initial.places);
  const [page, setPage] = useState(initial.page);

  const [draftState, setDraftState] = useState(initial.stateCode);
  const [draftScope, setDraftScope] = useState(initial.scope);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const [allMeasures, setAllMeasures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchKey, setFetchKey] = useState(0);

  const stateName = getStateName(selectedState) || selectedState;
  const isLocalScope = scope === BALLOT_SCOPE_LOCAL;

  const filteredMeasures = useMemo(
    () => filterBallotMeasures(allMeasures, {
      scope,
      places: appliedPlaces,
      query: appliedSearchQuery,
    }),
    [allMeasures, scope, appliedSearchQuery, appliedPlaces],
  );

  const { items: pageItems, totalCount, pageCount } = useMemo(
    () => paginateMeasures(filteredMeasures, page, PAGE_SIZE),
    [filteredMeasures, page],
  );

  const emptyState = useMemo(
    () => getBallotMeasuresEmptyState({
      selectedState,
      scope,
      stateName,
      query: appliedSearchQuery,
      places: appliedPlaces,
      allMeasures,
      filteredMeasures,
    }),
    [
      selectedState,
      scope,
      stateName,
      appliedSearchQuery,
      appliedPlaces,
      allMeasures,
      filteredMeasures,
    ],
  );

  const townSuggestions = useMemo(
    () => getLocalJurisdictions(allMeasures),
    [allMeasures],
  );

  // Mirrors of the applied queries, so the URL-sync effect can tell a real
  // navigation apart from the URL echo of a search the user just ran.
  const appliedSearchRef = useRef(initial.q);
  useEffect(() => {
    appliedSearchRef.current = appliedSearchQuery;
  }, [appliedSearchQuery]);

  const listSearchString = useMemo(
    () =>
      buildBallotListSearchParams({
        stateCode: selectedState,
        scope,
        query: appliedSearchQuery,
        places: isLocalScope ? appliedPlaces : [],
        page,
      }).toString(),
    [selectedState, scope, appliedSearchQuery, appliedPlaces, isLocalScope, page],
  );

  // Sync from URL when user navigates back. Only touch the text inputs when the
  // applied value actually changed, so an in-progress keystroke is never clobbered.
  useEffect(() => {
    const parsed = parseBallotListUrl(searchParams.toString());
    if (searchParams.get('view') !== 'ballot') return;
    setSelectedState(parsed.stateCode || userState || '');
    setScope(parsed.scope);
    if (appliedSearchRef.current !== parsed.q) setSearchQuery(parsed.q);
    setAppliedSearchQuery(parsed.q);
    setAppliedPlaces(parsed.places);
    setPage(parsed.page);
    setDraftState(parsed.stateCode || userState || '');
    setDraftScope(parsed.scope);
  }, [searchParams, userState]);

  // Persist ballot view URL
  useEffect(() => {
    const next = buildBallotListSearchParams({
      stateCode: selectedState,
      scope,
      query: appliedSearchQuery,
      places: isLocalScope ? appliedPlaces : [],
      page,
    });
    const curr = new URLSearchParams(searchParams.toString());
    if (normalizeSearchParamsForCompare(next) === normalizeSearchParamsForCompare(curr)) return;
    setSearchParams(next, { replace: true });
    onUrlChange?.(next.toString());
  }, [
    selectedState,
    scope,
    appliedSearchQuery,
    appliedPlaces,
    isLocalScope,
    page,
    setSearchParams,
    onUrlChange,
    searchParams,
  ]);

  useEffect(() => {
    if (!selectedState) {
      setAllMeasures([]);
      setLoading(false);
      setError(null);
      return;
    }

    let isCurrent = true;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const res = await fetchPublicDWStateElections(selectedState);
        if (!isCurrent) return;
        if (res.error) {
          setError(res.error);
          setAllMeasures([]);
          return;
        }
        const measures = extractMeasuresFromElections(res.elections, selectedState);
        setAllMeasures(measures);
      } catch (err) {
        if (!isCurrent) return;
        setError(err.message || 'Failed to load ballot measures.');
        setAllMeasures([]);
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    load();
    return () => {
      isCurrent = false;
    };
  }, [selectedState, fetchKey]);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleSearch = (value) => {
    setAppliedSearchQuery(value);
    setPage(1);
    scrollToTop();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setAppliedSearchQuery('');
    setPage(1);
    scrollToTop();
  };

  const handleClearPlaces = () => {
    setAppliedPlaces([]);
    setPage(1);
    scrollToTop();
  };

  const handleScopeChange = (nextScope) => {
    const normalized = normalizeBallotScope(nextScope);
    if (normalized === scope) return;
    setScope(normalized);
    setPage(1);
    scrollToTop();
  };

  const handleTownSelect = (towns) => {
    setAppliedPlaces(towns);
    setPage(1);
    scrollToTop();
  };

  const handleFilterApply = () => {
    if (draftState !== selectedState) setAppliedPlaces([]);
    setSelectedState(draftState);
    setScope(normalizeBallotScope(draftScope));
    setPage(1);
    scrollToTop();
  };

  const handleClearFilters = () => {
    const resetState = userState || '';
    setSelectedState(resetState);
    setDraftState(resetState);
    setScope(BALLOT_SCOPE_DEFAULT);
    setDraftScope(BALLOT_SCOPE_DEFAULT);
    setSearchQuery('');
    setAppliedSearchQuery('');
    setAppliedPlaces([]);
    setPage(1);
    setFilterModalOpen(false);
    scrollToTop();
  };

  const handleRetry = () => setFetchKey((k) => k + 1);

  // Only count the state as a filter for users who have a home state to differ from.
  const stateIsCustom = Boolean(userState) && selectedState !== userState;
  const filterBadgeCount =
    (scope !== BALLOT_SCOPE_DEFAULT ? 1 : 0) +
    (isLocalScope ? appliedPlaces.length : 0) +
    (stateIsCustom ? 1 : 0);
  const showClear =
    appliedSearchQuery !== '' ||
    (isLocalScope && appliedPlaces.length > 0) ||
    scope !== BALLOT_SCOPE_DEFAULT ||
    stateIsCustom;

  if (!selectedState && !loading) {
    return (
      <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', mb: 2 }}>
          Select a state to browse ballot measures
        </Typography>
        <Button
          variant="contained"
          onClick={() => setFilterModalOpen(true)}
          sx={{ bgcolor: PRIMARY, textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#065f46' } }}
        >
          Choose state
        </Button>
        <BallotMeasuresFilterModal
          open={filterModalOpen}
          onClose={() => setFilterModalOpen(false)}
          draftStateCode={draftState}
          onDraftStateChange={setDraftState}
          draftScope={draftScope}
          onDraftScopeChange={setDraftScope}
          userState={userState}
          onApply={handleFilterApply}
        />
      </Box>
    );
  }

  return (
    <Box className="ballot-measures-panel">
      <Box className="bills-header bills-header--compact">
        <Box className="bills-header__search-wrap">
          <BillsSearchBar
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onSearch={handleSearch}
            placeholder={`Search ${isLocalScope ? 'local' : 'statewide'} measures…`}
          />
          {showClear && (
            <Button onClick={handleClearFilters} className="bills-header__clear">
              Clear Filters
            </Button>
          )}
        </Box>
        <Box className="bills-header__actions">
          <Badge
            badgeContent={filterBadgeCount}
            color="primary"
            invisible={filterBadgeCount === 0}
            className="bills-header__filter-badge"
          >
            <IconButton
              aria-label="Open ballot measure filters"
              onClick={() => {
                setDraftState(selectedState);
                setDraftScope(scope);
                setFilterModalOpen(true);
              }}
              className="bills-header__filter-btn bills-header__filter-btn--active"
              size="medium"
            >
              <FilterListRoundedIcon />
            </IconButton>
          </Badge>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
        {BALLOT_SCOPE_CHIP_OPTIONS.map((chip) => (
          <Chip
            key={chip.id}
            label={chip.label}
            size="small"
            onClick={() => handleScopeChange(chip.id)}
            sx={{
              fontWeight: 600,
              cursor: 'pointer',
              bgcolor: scope === chip.id ? PRIMARY : '#fff',
              color: scope === chip.id ? '#fff' : '#0f172a',
              border: scope === chip.id ? `1px solid ${PRIMARY}` : '1px solid #cbd5e1',
              '&:hover': {
                bgcolor: scope === chip.id ? '#065f46' : '#f1f5f9',
              },
            }}
          />
        ))}
        <Typography sx={{ fontSize: 12, color: '#64748b', ml: 0.5 }}>
          {stateName}
        </Typography>
      </Box>

      {isLocalScope && (
        <Autocomplete
          multiple
          limitTags={3}
          disableCloseOnSelect
          options={townSuggestions}
          getOptionLabel={(option) => `${option.name} (${option.count})`}
          value={townSuggestions.filter((town) => appliedPlaces.includes(town.name))}
          onChange={(_, next) => handleTownSelect(next.map((town) => town.name))}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          ListboxProps={{
            sx: {
              maxHeight: 280,
              '& .MuiAutocomplete-option': { minHeight: 44 },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                appliedPlaces.length
                  ? 'Add another…'
                  : 'Towns, cities, and counties'
              }
              inputProps={{
                ...params.inputProps,
                'aria-label': 'Select towns, cities, and counties',
              }}
            />
          )}
          sx={{
            width: '100%',
            maxWidth: 736,
            mb: 1.5,
            bgcolor: '#fff',
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              minHeight: 43,
              alignItems: 'center',
              flexWrap: 'wrap',
              ...(appliedPlaces.length === 0 ? { height: 43 } : { py: 0.5 }),
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e2e8f0',
              },
            },
            '& .MuiChip-root': { height: 24, fontSize: 13 },
            '& .MuiAutocomplete-input': {
              minWidth: '30px !important',
              py: '0 !important',
            },
          }}
        />
      )}

      {isLocalScope && !loading && !error && (
        <Box sx={{ mb: 2 }}>
          {appliedPlaces.length > 0 ? (
            <Typography sx={{ fontSize: 13, color: '#334155' }}>
              {totalCount === 1 ? 'Showing 1 measure for selected places' : `Showing ${totalCount} measures for selected places`}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 13, color: '#334155' }}>
              Showing all local measures. Select places above to narrow the list.
            </Typography>
          )}
        </Box>
      )}

      <BallotMeasuresFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        draftStateCode={draftState}
        onDraftStateChange={setDraftState}
        draftScope={draftScope}
        onDraftScopeChange={setDraftScope}
        userState={userState}
        onApply={handleFilterApply}
      />

      {loading && allMeasures.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Box>
      )}

      {error && (
        <Box
          sx={{
            py: 4,
            px: 2,
            textAlign: 'center',
            borderRadius: '10px',
            border: '1px solid #fecaca',
            bgcolor: '#fef2f2',
          }}
        >
          <Typography sx={{ color: '#b91c1c', fontWeight: 600, mb: 1 }}>{error}</Typography>
          <Button onClick={handleRetry} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Try again
          </Button>
        </Box>
      )}

      {!error && !loading && pageItems.length === 0 && emptyState && (
        <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
          <InfoOutlinedIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', mb: 1 }}>
            {emptyState.title}
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#334155', maxWidth: 520, mx: 'auto', lineHeight: 1.55 }}>
            {emptyState.message}
          </Typography>

          {emptyState.showClearSearch && (
            <Button
              onClick={handleClearSearch}
              sx={{ mt: 2, textTransform: 'none', fontWeight: 600, color: PRIMARY }}
            >
              Clear search
            </Button>
          )}

          {emptyState.showClearPlaces && (
            <Button
              onClick={handleClearPlaces}
              sx={{ mt: emptyState.showClearSearch ? 1 : 2, textTransform: 'none', fontWeight: 600, color: PRIMARY }}
            >
              Clear selected places
            </Button>
          )}

          {emptyState.showBrowseLocal && (
            <Button
              variant="contained"
              onClick={() => handleScopeChange(BALLOT_SCOPE_LOCAL)}
              sx={{
                mt: 2,
                bgcolor: PRIMARY,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: '#065f46' },
              }}
            >
              {`Browse ${emptyState.browseLocalCount} local measures`}
            </Button>
          )}

          {emptyState.showBrowseStatewide && (
            <Button
              variant="contained"
              onClick={() => handleScopeChange(BALLOT_SCOPE_STATE)}
              sx={{
                mt: 2,
                bgcolor: PRIMARY,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: '#065f46' },
              }}
            >
              {`Browse ${emptyState.browseStatewideCount} statewide measures`}
            </Button>
          )}
        </Box>
      )}

      {loading && allMeasures.length > 0 && (
        <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />
      )}

      {!error && pageItems.length > 0 && (
        <>
          <Box
            className="bills-grid"
            sx={{
              width: '100%',
              opacity: loading ? 0.55 : 1,
              transition: 'opacity 0.2s ease',
            }}
          >
            {pageItems.map((measure) => (
              <BallotMeasureCard
                key={measure.id}
                measure={measure}
                listSearch={listSearchString}
              />
            ))}
          </Box>

          {pageCount > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, value) => {
                  setPage(value);
                  scrollToTop();
                }}
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}

          <Typography sx={{ fontSize: 12, color: '#64748b', textAlign: 'center', mt: 1, mb: 2 }}>
            Ballot data sourced from Ballotpedia via Democracy Works.
          </Typography>
        </>
      )}
    </Box>
  );
}
